import * as React from 'react';
import { cn } from '@/lib/utils';
import { Combobox, ComboboxChip, ComboboxChips, ComboboxChipsInput, ComboboxEmpty, ComboboxItem, ComboboxList, ComboboxPopup, ComboboxValue } from '@/components/ui/combobox';

export const ComboboxMultiple = React.forwardRef(
  (
    {
      className,
      options = [],
      labelKey = 'label',
      valueKey = 'value',
      placeholder = 'Selecione...',
      loading = false,
      emptyMessage = 'Nenhum resultado encontrado.',
      value = [], // array de IDs
      onValueChange,
      disabled = false,
      ...props
    },
    ref
  ) => {
    // 🔁 IDs -> objetos
    const selectedItems = React.useMemo(() => {
      if (!Array.isArray(value)) return [];

      return value
        .map(val => {
          if (typeof val === 'object') return val;

          return options.find(opt => opt?.[valueKey] === val) ?? null;
        })
        .filter(Boolean);
    }, [value, options, valueKey]);

    // 🔁 objetos -> IDs
    const handleChange = React.useCallback(
      items => {
        if (!onValueChange) return;

        if (!Array.isArray(items)) {
          onValueChange([]);
          return;
        }

        const ids = items.map(item => item?.[valueKey]).filter(Boolean);

        onValueChange(ids);
      },
      [onValueChange, valueKey]
    );

    const getItemText = React.useCallback(
      item => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return item?.[labelKey] ?? '';
      },
      [labelKey]
    );

    return (
      <Combobox
        ref={ref}
        items={options}
        multiple
        className={cn('w-full', className)}
        getItemText={getItemText}
        value={selectedItems}
        onValueChange={handleChange}
        equals={(a, b) => a?.[valueKey] === b?.[valueKey]}
        disabled={disabled}
        {...props}
      >
        <ComboboxChips>
          <ComboboxValue>
            {items => (
              <>
                {items?.map(item => (
                  <ComboboxChip
                    key={item?.[valueKey]}
                    aria-label={getItemText(item)}
                  >
                    {getItemText(item)}
                  </ComboboxChip>
                ))}

                <ComboboxChipsInput
                  placeholder={items?.length ? '' : placeholder}
                  aria-label={placeholder}
                />
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>

        <ComboboxPopup>
          <ComboboxEmpty>{loading ? 'Carregando opções...' : emptyMessage}</ComboboxEmpty>

          <ComboboxList>
            {item => (
              <ComboboxItem
                key={item?.[valueKey]}
                value={item}
              >
                {getItemText(item)}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxPopup>
      </Combobox>
    );
  }
);

ComboboxMultiple.displayName = 'ComboboxMultiple';
